/**
 * Extracts the pill-level enrichment fields from a Visit entity, producing
 * a plain-data snapshot that survives after the live Visit is removed from
 * the entity adapter (Yard-5946: retained Visit-derived puck metadata).
 *
 * This is a pure function — no side effects, no mutations of the input.
 * The returned object is a plain Redux-storable value (no Object.freeze,
 * no class instances). Arrays are cloned to avoid sharing mutable
 * references with the caller's payload.
 *
 * @see PillEnrichmentData (the full type); this returns everything EXCEPT
 *      `eventSummaries` (events are independent — they continue after Visit
 *      removal and are appended separately by the selector).
 */

import type { PillEnrichmentData } from "@/features/ryteTracker/types/enrichedPuck";
import type { VersionedVisit } from "@/features/ryteTracker/store/ryteDataSlice";
import { deriveEquipmentTagVariants } from "@/features/ryteTracker/utils/format/deriveEquipmentTagVariants";
import type { EquipmentTagVariant } from "@/features/ryteTracker/utils/format/deriveEquipmentTagVariants";

/**
 * Retained subset of `PillEnrichmentData` — everything the pill needs
 * from a Visit except event summaries (which are independently sourced).
 */
export type RetainedVisitEnrichment = Omit<PillEnrichmentData, "eventSummaries">;

/**
 * Builds a plain-data enrichment snapshot from a Visit entity.
 * All arrays are cloned (shallow copy) so the returned object does not
 * share mutable references with the incoming payload.
 */
export const buildVisitEnrichment = (visit: VersionedVisit): RetainedVisitEnrichment => {
  const equipmentDetails = visit.data?.equipment?.equipmentDetails;
  const firstActiveReason = visit.data?.equipment?.visitDetails?.activeVisitReasons?.[0];
  const equipmentTagVariants: EquipmentTagVariant[] = deriveEquipmentTagVariants(
    visit.data?.equipment
  );

  // Build trailer tokens — deduped, non-empty, order-stable.
  const rawTokens = [
    equipmentDetails?.equipmentNumber,
    equipmentDetails?.type,
    equipmentDetails?.ownerCode,
    equipmentDetails?.fleetSystemId,
    equipmentDetails?.licensePlate?.licensePlateNumber,
  ];
  const seenTokens = new Set<string>();
  const trailerTokensArr: string[] = [];
  for (const t of rawTokens) {
    if (!t) continue;
    if (seenTokens.has(t)) continue;
    seenTokens.add(t);
    trailerTokensArr.push(t);
  }

  return {
    carrierCode: equipmentDetails?.ownerCode ?? undefined,
    equipmentNumber: equipmentDetails?.equipmentNumber ?? undefined,
    equipmentType: equipmentDetails?.type ?? undefined,
    isDrop: firstActiveReason ? firstActiveReason.isDrop : undefined,
    isLiveLoad: firstActiveReason ? firstActiveReason.isLiveLoad : undefined,
    // Clone to avoid sharing the mutable array from deriveEquipmentTagVariants.
    equipmentTagVariants: equipmentTagVariants.length > 0 ? [...equipmentTagVariants] : undefined,
    arrivalAtLocationUTC: visit.data?.equipment?.arrivalAtLocationUTC ?? undefined,
    // Clone to avoid sharing mutable external payload array.
    trailerTokens: trailerTokensArr.length > 0 ? [...trailerTokensArr] : undefined,
  };
};
